import { create } from 'zustand'

/**
 * The controls in the Host Controls dialog, mirrored from the room.
 * Nine of them.
 *
 * Each flag answers "are attendees ALLOWED to do this?":
 *   - true  → attendees can do the action
 *   - false → blocked for attendees (host and co-host are exempt)
 *
 * **The room owns these, not this store.** `RoomCoordinator.host_controls`
 * holds them server-side and pushes the full set on join, so every participant
 * — including a late one — agrees on the rules. This store is the local mirror:
 * seeded by the `host:controls` push, republished by a moderator via
 * `host:set_controls`. The defaults below only apply before the room has
 * answered, and they match the server's own defaults key for key so that window
 * is invisible. **Change one and change the other** (`@default_host_controls` in
 * `lib/signaling/rooms/room_coordinator.ex`).
 *
 * **Mirrored is not enforced.** Only `sendChat`/`privateChat` and
 * `allowAnnotation` are refused server-side; the rest are honoured by our own
 * UI and a client that ignored them would not be stopped. Treat them as
 * controls, not as security.
 *
 * Deliberately NOT here: `allowHandRaise` and `allowQuestions`. They belong to
 * the Engagement Controls dialog, they are not on this dialog, and the room
 * does not track them — they stay local in `host-permissions.store.ts`. The two
 * dialogs do overlap on five flags (chat, reactions, audio, video, screen
 * share), and those five live HERE, so a change from either surface is the same
 * change.
 */
export type MeetingHostControls = {
  /** Participants can send messages in the group chat. Enforced server-side. */
  sendChat: boolean
  /** Participants can start a 1-on-1 private chat. Enforced server-side. */
  privateChat: boolean
  /**
   * The chat panel is available AT ALL. When false the chat button disappears
   * and an open panel closes — **for everyone, host and co-host included**.
   *
   * This is the one control moderators are not exempt from, because it is a
   * feature switch for the room rather than a restriction on attendees. A host
   * who turns chat off and keeps their own is not what the switch says, and
   * they would be left with a panel and no button to close it. Contrast
   * `sendChat`, which restricts who may speak and does exempt moderators.
   */
  allowChat: boolean
  /** Participants can unmute their microphone. */
  useMicrophone: boolean
  /** Participants can turn on their camera. */
  useVideo: boolean
  /** Participants can share their screen. */
  shareScreen: boolean
  /** Participants can attach files in chat. */
  sendFiles: boolean
  /** Participants can send emoji reactions. */
  useEmojis: boolean
  /**
   * "Enable annotation", the master switch. On: participants may ask for
   * annotation access (the pen over a document) and those granted it draw —
   * presenting a document is not access. Off: only the host and co-hosts
   * draw; grants are kept for when it is back on. Enforced server-side.
   */
  allowAnnotation: boolean
}

type MeetingHostControlsState = MeetingHostControls & {
  /** Replace a single control. */
  setControl: <K extends keyof MeetingHostControls>(key: K, value: MeetingHostControls[K]) => void
  /** Merge several controls at once — how an inbound `host:controls` is applied. */
  setControls: (patch: Partial<MeetingHostControls>) => void
  /** Back to the defaults below. */
  reset: () => void
}

const defaults: MeetingHostControls = {
  sendChat: true,
  // Private chat and file sharing start on so a new meeting feels
  // feature-complete out of the box; a host restricts, and never has to
  // un-restrict before the meeting works.
  privateChat: true,
  allowChat: true,
  useMicrophone: true,
  useVideo: true,
  shareScreen: true,
  sendFiles: true,
  useEmojis: true,
  allowAnnotation: true,
}

export const useMeetingHostControlsStore = create<MeetingHostControlsState>((set) => ({
  ...defaults,
  setControl: (key, value) => set({ [key]: value } as Partial<MeetingHostControlsState>),
  setControls: (patch) => set(patch),
  reset: () => set({ ...defaults }),
}))

/**
 * The flags alone, without the actions — the shape that crosses the wire.
 *
 * Memoised on the values rather than rebuilt per render: this feeds a
 * `useEffect` dependency that publishes to the room, and a fresh object every
 * render would republish the same map forever.
 */
let controlsCache: MeetingHostControls | null = null
export const selectMeetingHostControls = (s: MeetingHostControlsState): MeetingHostControls => {
  const next: MeetingHostControls = {
    sendChat: s.sendChat,
    privateChat: s.privateChat,
    allowChat: s.allowChat,
    useMicrophone: s.useMicrophone,
    useVideo: s.useVideo,
    shareScreen: s.shareScreen,
    sendFiles: s.sendFiles,
    useEmojis: s.useEmojis,
    allowAnnotation: s.allowAnnotation,
  }
  if (
    controlsCache &&
    (Object.keys(next) as (keyof MeetingHostControls)[]).every((k) => controlsCache![k] === next[k])
  ) {
    return controlsCache
  }
  controlsCache = next
  return next
}

/** Selector helpers — keep call sites lean and stable. */
export const selectCanSendChat = (s: MeetingHostControlsState) => s.sendChat
export const selectAllowChat = (s: MeetingHostControlsState) => s.allowChat
export const selectCanPrivateChat = (s: MeetingHostControlsState) => s.privateChat
export const selectCanSendFiles = (s: MeetingHostControlsState) => s.sendFiles
export const selectCanUseMicrophone = (s: MeetingHostControlsState) => s.useMicrophone
export const selectCanUseVideo = (s: MeetingHostControlsState) => s.useVideo
export const selectCanShareScreen = (s: MeetingHostControlsState) => s.shareScreen
export const selectCanUseEmojis = (s: MeetingHostControlsState) => s.useEmojis
export const selectCanAnnotate = (s: MeetingHostControlsState) => s.allowAnnotation
